import BigDataChart from "@/components/ui/chart/BigDataChart";
import DonutChart from "@/components/ui/chart/DonutChart";
import HorizontalBarChart from "@/components/ui/chart/HorizontalBarChart";
import LineChart from "@/components/ui/chart/LineChart";
import VerticalBarChart from "@/components/ui/chart/VerticalBarChart";
import type useDatasetWidgetSummary from "@/hooks/dataset-widget/useDatasetWidgetSummary";
import { WidgetConfig, WidgetType } from "@/types/widget";
import { removeSpecialCharacters } from "@/utils/string";

type WidgetSummary = ReturnType<typeof useDatasetWidgetSummary>;

interface DatasetWidgetChartProps {
  readonly widget: WidgetConfig;
  readonly rawData: WidgetSummary["rawData"];
  readonly groupedData: WidgetSummary["groupedData"];
  readonly lineSeries: WidgetSummary["lineSeries"];
  readonly bigNumberData: WidgetSummary["bigNumberData"];
}

const CHART_HEIGHT = 450;

/** Row key holding the dimension value when the widget data is not grouped. */
export const UNGROUPED_CATEGORY_KEY = "label";

/** Renders the chart matching `widget.chart_type` from the widget summary data. */
export default function DatasetWidgetChart({
  widget,
  rawData,
  groupedData,
  lineSeries,
  bigNumberData
}: DatasetWidgetChartProps) {
  const ungroupedData = rawData.map((item) => ({
    label: item.label,
    value: Number(item.value)
  }));

  return (
    <>
      {widget.chart_type === WidgetType.BIG_NUMBER && bigNumberData && (
        <BigDataChart
          value={bigNumberData.value}
          type={bigNumberData.type}
          measure={bigNumberData.measure}
        />
      )}
      {widget.chart_type === WidgetType.BAR_H &&
        (groupedData
          ? !!groupedData.rows.length && (
              <HorizontalBarChart
                data={groupedData.rows}
                categoryKey={groupedData.nameKey}
                series={groupedData.series}
                height={CHART_HEIGHT}
              />
            )
          : !!ungroupedData.length && (
              <HorizontalBarChart
                data={ungroupedData}
                categoryKey={UNGROUPED_CATEGORY_KEY}
                series={lineSeries}
                aggregation={removeSpecialCharacters(widget.aggregation)}
                height={CHART_HEIGHT}
              />
            ))}
      {widget.chart_type === WidgetType.DONUT && !!rawData.length && (
        <DonutChart
          data={rawData.map((item) => ({
            name: item.label,
            value: Number(item.value)
          }))}
          height={CHART_HEIGHT}
        />
      )}
      {widget.chart_type === WidgetType.BAR_V &&
        (groupedData
          ? !!groupedData.rows.length && (
              <VerticalBarChart
                data={groupedData.rows}
                categoryKey={groupedData.nameKey}
                series={groupedData.series}
                isResponsive={false}
                height={CHART_HEIGHT}
              />
            )
          : !!ungroupedData.length && (
              <VerticalBarChart
                data={ungroupedData}
                categoryKey={UNGROUPED_CATEGORY_KEY}
                series={lineSeries}
                aggregation={removeSpecialCharacters(widget.aggregation)}
                height={CHART_HEIGHT}
              />
            ))}
      {widget.chart_type === WidgetType.LINE &&
        (groupedData
          ? !!groupedData.rows.length && (
              <LineChart
                data={groupedData.rows}
                categoryKey={groupedData.nameKey}
                series={groupedData.series}
                height={CHART_HEIGHT}
              />
            )
          : !!ungroupedData.length && (
              <LineChart
                data={ungroupedData}
                categoryKey={UNGROUPED_CATEGORY_KEY}
                series={lineSeries}
                aggregation={removeSpecialCharacters(widget.aggregation)}
                height={CHART_HEIGHT}
              />
            ))}
    </>
  );
}
